import { useEffect, useRef, useState } from "react"

const CopyValueButton = ({ label, value }) => {
  const [copy_state, set_copy_state] = useState("idle")
  const timeout_ref = useRef(null)

  useEffect(() => {
    return () => {
      if (timeout_ref.current) {
        clearTimeout(timeout_ref.current)
      }
    }
  }, [])

  if (value === undefined || value === null || value === "") {
    return null
  }

  const str = String(value)

  const schedule_reset = () => {
    if (timeout_ref.current) {
      clearTimeout(timeout_ref.current)
    }
    timeout_ref.current = setTimeout(() => {
      set_copy_state("idle")
    }, 1600)
  }

  const on_copy_click = async () => {
    try {
      await navigator.clipboard.writeText(str)
      set_copy_state("copied")
    }
    catch (error) {
      console.log(error)
      set_copy_state("failed")
    }
    schedule_reset()
  }

  const button_label =
    copy_state === "copied"
      ? "Copied"
      : copy_state === "failed"
        ? "Failed"
        : "Copy"

  const button_class =
    copy_state === "copied"
      ? "border-emerald-400 bg-emerald-50 text-emerald-800"
      : copy_state === "failed"
        ? "border-red-300 bg-red-50 text-red-800"
        : "border-gray-300 bg-white text-gray-600 hover:border-blue-400 hover:text-blue-700"

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
      <span className="font-semibold text-gray-800">{label}:</span>
      <span className="break-all text-gray-700">{str}</span>
      <button
        type="button"
        className={`shrink-0 rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors duration-150 ${button_class}`}
        onClick={() => on_copy_click()}
      >
        {button_label}
      </button>
    </div>
  )
}

const format_date_time = (value) => {
  if (!value) {
    return null
  }
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) {
    return String(value)
  }
  return dt.toLocaleString()
}

const KeyValueGrid = ({ rows }) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return null
  }
  return (
    <div className="mt-2 grid grid-cols-1 gap-1 text-xs text-gray-700 sm:grid-cols-2">
      {rows.map((row) => (
        <div key={`${row.label}:${row.value}`} className="flex items-center gap-2">
          <span className="font-semibold text-gray-800">{row.label}:</span>
          <span className="break-all">{row.value}</span>
        </div>
      ))}
    </div>
  )
}

const StatusBadge = ({ status }) => {
  const normalized = String(status || "unknown").toLowerCase()
  let color = "bg-gray-100 text-gray-700"
  if (normalized === "open" || normalized === "active") {
    color = "bg-emerald-100 text-emerald-700"
  }
  else if (normalized === "closed" || normalized === "inactive") {
    color = "bg-amber-100 text-amber-700"
  }
  else if (normalized === "finalized" || normalized === "resolved" || normalized === "determined") {
    color = "bg-indigo-100 text-indigo-700"
  }
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${color}`}>
      {status || "unknown"}
    </span>
  )
}

const IdentityRows = ({ item }) => {
  const display = item.display || {}
  const secondary_ids = Array.isArray(display.secondary_ids) ? display.secondary_ids : []
  return (
    <div className="mt-2 space-y-1">
      <CopyValueButton label="primary_id" value={display.primary_id || item.item_id} />
      {secondary_ids.map((kv) => (
        <CopyValueButton key={`${kv.label}:${kv.value}`} label={kv.label} value={kv.value} />
      ))}
    </div>
  )
}

const EventDetails = ({ item }) => {
  const display = item.display || {}
  const timing_rows = (display.timing || []).map((row) => ({
    ...row,
    value: format_date_time(row.value),
  })).filter((row) => row.value)

  return (
    <>
      <IdentityRows item={item} />
      {display.market_count !== null && display.market_count !== undefined && (
        <div className="mt-2 text-xs text-gray-700">
          <span className="font-semibold text-gray-800">market_count:</span> {display.market_count}
        </div>
      )}
      <KeyValueGrid rows={timing_rows} />
      <KeyValueGrid rows={display.meta || []} />
      <KeyValueGrid rows={display.resolution || []} />
    </>
  )
}

const MarketDetails = ({ item }) => {
  const display = item.display || {}
  const timing_rows = (display.timing || []).map((row) => ({
    ...row,
    value: format_date_time(row.value),
  })).filter((row) => row.value)
  return (
    <>
      <IdentityRows item={item} />
      <KeyValueGrid rows={display.pricing || []} />
      <KeyValueGrid rows={display.activity || []} />
      <KeyValueGrid rows={timing_rows} />
      <KeyValueGrid rows={display.meta || []} />
      <KeyValueGrid rows={display.resolution || []} />
    </>
  )
}

const DiscoverResults = ({
  section_title,
  filtered_items,
  is_loading,
  fetch_error_text,
  api_hint_for_errors,
  empty_message,
}) => {
  const hide_title_row = section_title === null
  const title =
    section_title === null || section_title === undefined || section_title === ""
      ? "Results"
      : section_title

  return (
    <div className="mt-0 space-y-4 rounded-lg border border-gray-200 bg-white p-4">
      {!hide_title_row && (
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-900">{title}</div>
          {is_loading && (
            <div className="text-xs font-semibold text-blue-600">Loading data...</div>
          )}
        </div>
      )}
      {hide_title_row && is_loading && (
        <div className="flex justify-end">
          <div className="text-xs font-semibold text-blue-600">Loading data...</div>
        </div>
      )}

      {fetch_error_text && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <div className="font-semibold">Fetch error</div>
          <div className="mt-1">{fetch_error_text}</div>
          {api_hint_for_errors && (
            <div className="mt-1 text-xs">
              API: <span className="font-semibold">{api_hint_for_errors}</span>
            </div>
          )}
        </div>
      )}

      {!is_loading && filtered_items.length === 0 && (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          {empty_message || "No items match your current search and filters."}
        </div>
      )}

      <div className="space-y-3">
        {filtered_items.map((item) => (
          <div key={`${item.provider}-${item.entity}-${item.item_id}`} className="rounded-md border border-gray-200 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                {item.provider}
              </span>
              <span className="rounded bg-purple-50 px-2 py-0.5 text-xs font-semibold text-purple-700">
                {item.entity}
              </span>
              <StatusBadge status={item.status} />
            </div>

            <div className="mt-2 text-sm font-semibold text-gray-900">{item.title}</div>
            {item.description && (
              <div className="mt-1 text-sm text-gray-600">{item.description}</div>
            )}

            {item.entity === "event" ? <EventDetails item={item} /> : <MarketDetails item={item} />}

            <div className="mt-2 text-xs text-gray-700">
              <span className="font-semibold text-gray-800">tags:</span>{" "}
              {item.tag_list.length > 0 ? item.tag_list.join(", ") : "none"}
            </div>

            <details className="mt-3">
              <summary className="cursor-pointer text-xs font-semibold text-gray-700">
                Raw item data
              </summary>
              <pre className="mt-2 max-h-72 overflow-auto rounded bg-gray-900 p-3 text-xs text-gray-100">
                {JSON.stringify(item.raw_item, null, 2)}
              </pre>
            </details>
          </div>
        ))}
      </div>
    </div>
  )
}

export default DiscoverResults
